import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Phone, Mail, Send, Briefcase, Upload, FileText, X } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const MAX_CV_BYTES = 10 * 1024 * 1024; // 10 MB
const ACCEPTED_CV = ".pdf,.doc,.docx,.rtf,.txt";

/**
 * Safely stringify an axios/backend error detail into a user-readable message.
 * FastAPI 422 responses return `detail` as an array of objects, which if passed
 * directly to a React child crashes with "Objects are not valid as a React child".
 */
const extractErrorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const msgs = detail
      .map((d) => (typeof d === "string" ? d : d?.msg))
      .filter(Boolean);
    if (msgs.length) return msgs.join(", ");
  }
  if (detail && typeof detail === "object" && detail.msg) return String(detail.msg);
  return fallback;
};

export const CareerModal = ({ isOpen, onClose, selectedRole = "" }) => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    role: selectedRole,
    experience: "",
    message: ""
  });
  const [cvFile, setCvFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Keep the role in sync whenever the parent opens the modal with a preset role.
  // Without this, the `role` field stays empty and submissions fail with a
  // "role field required" 422 from the backend.
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({ ...prev, role: selectedRole || "" }));
    }
  }, [isOpen, selectedRole]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCvChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCvFile(null);
      return;
    }
    if (file.size > MAX_CV_BYTES) {
      toast.error("CV file is too large. Maximum size is 10 MB.");
      e.target.value = "";
      return;
    }
    setCvFile(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("name", formData.name);
      payload.append("phone", formData.phone);
      payload.append("email", formData.email);
      payload.append("role", formData.role);
      payload.append("experience", formData.experience || "");
      payload.append("message", formData.message || "");
      if (cvFile) {
        payload.append("cv", cvFile);
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/careers/application`,
        payload,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 201) {
        toast.success("Thank you for your application! We'll be in touch soon.");
        setFormData({
          name: "",
          phone: "",
          email: "",
          role: "",
          experience: "",
          message: ""
        });
        setCvFile(null);
        onClose();
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      const msg = extractErrorMessage(
        error,
        "Something went wrong. Please try again or email us directly."
      );
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto"
        data-testid="career-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-teal-700 flex items-center gap-2">
            <Briefcase className="h-6 w-6" />
            Apply for a Position
          </DialogTitle>
        </DialogHeader>

        {/* Quick Contact Info */}
        <div className="bg-teal-50 rounded-lg p-4 mb-4">
          <p className="text-sm text-teal-700 font-medium mb-2">Questions about careers?</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <a 
              href="tel:01516775573" 
              className="flex items-center gap-1 text-teal-700 hover:text-teal-800"
            >
              <Phone className="h-4 w-4" />
              0151 677 5573
            </a>
            <a 
              href="mailto:careers@barnstoncourt.com" 
              className="flex items-center gap-1 text-teal-700 hover:text-teal-800"
            >
              <Mail className="h-4 w-4" />
              careers@barnstoncourt.com
            </a>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="career-name">Your Name *</Label>
            <Input
              id="career-name"
              data-testid="career-name-input"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              placeholder="John Smith"
              required
              className="mt-1"
            />
          </div>

          {/* Phone & Email Row */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="career-phone">Phone Number *</Label>
              <Input
                id="career-phone"
                data-testid="career-phone-input"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleChange("phone", e.target.value)}
                placeholder="0151 234 5678"
                required
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="career-email">Email Address *</Label>
              <Input
                id="career-email"
                data-testid="career-email-input"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="john@example.com"
                required
                className="mt-1"
              />
            </div>
          </div>

          {/* Role */}
          <div>
            <Label htmlFor="career-role">Position Applying For *</Label>
            <Select
              value={formData.role}
              onValueChange={(value) => handleChange("role", value)}
              required
            >
              <SelectTrigger className="mt-1" data-testid="career-role-select">
                <SelectValue placeholder="Select a position" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Care Assistant">Care Assistant</SelectItem>
                <SelectItem value="Senior Care Assistant">Senior Care Assistant</SelectItem>
                <SelectItem value="Registered Nurse">Registered Nurse</SelectItem>
                <SelectItem value="Night Care Assistant">Night Care Assistant</SelectItem>
                <SelectItem value="Kitchen Assistant">Kitchen Assistant</SelectItem>
                <SelectItem value="Housekeeper">Housekeeper</SelectItem>
                <SelectItem value="Housekeeping">Housekeeping</SelectItem>
                <SelectItem value="Activities Coordinator">Activities Coordinator</SelectItem>
                <SelectItem value="Manager">Manager</SelectItem>
                <SelectItem value="Maintenance">Maintenance</SelectItem>
                <SelectItem value="Chef">Chef</SelectItem>
                <SelectItem value="Admin">Admin</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Experience */}
          <div>
            <Label htmlFor="career-experience">Relevant Experience</Label>
            <Select
              value={formData.experience}
              onValueChange={(value) => handleChange("experience", value)}
            >
              <SelectTrigger className="mt-1" data-testid="career-experience-select">
                <SelectValue placeholder="Select your experience level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="No experience">No experience (training provided)</SelectItem>
                <SelectItem value="Less than 1 year">Less than 1 year</SelectItem>
                <SelectItem value="1-2 years">1-2 years</SelectItem>
                <SelectItem value="3-5 years">3-5 years</SelectItem>
                <SelectItem value="5+ years">5+ years</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Message */}
          <div>
            <Label htmlFor="career-message">Tell us about yourself</Label>
            <Textarea
              id="career-message"
              data-testid="career-message-input"
              value={formData.message}
              onChange={(e) => handleChange("message", e.target.value)}
              placeholder="Share why you'd like to work at Barnston Court and any relevant skills or qualifications..."
              rows={4}
              className="mt-1"
            />
          </div>

          {/* CV / Resume Upload */}
          <div>
            <Label htmlFor="career-cv">Upload Your CV (optional)</Label>
            <div className="mt-1">
              {!cvFile ? (
                <label
                  htmlFor="career-cv"
                  className="flex flex-col items-center justify-center w-full px-4 py-6 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50/40 transition-colors"
                  data-testid="career-cv-dropzone"
                >
                  <Upload className="h-6 w-6 text-gray-400 mb-2" />
                  <span className="text-sm font-medium text-gray-700">Click to upload your CV</span>
                  <span className="text-xs text-gray-500 mt-1">PDF, DOC, DOCX, RTF or TXT &middot; max 10 MB</span>
                  <input
                    id="career-cv"
                    data-testid="career-cv-input"
                    type="file"
                    accept={ACCEPTED_CV}
                    onChange={handleCvChange}
                    className="hidden"
                  />
                </label>
              ) : (
                <div
                  className="flex items-center justify-between gap-3 px-4 py-3 border border-teal-200 bg-teal-50 rounded-lg"
                  data-testid="career-cv-selected"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="bg-teal-600 text-white p-2 rounded-md flex-shrink-0">
                      <FileText className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{cvFile.name}</p>
                      <p className="text-xs text-gray-500">
                        {(cvFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setCvFile(null)}
                    className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                    aria-label="Remove CV"
                    data-testid="career-cv-remove-btn"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            data-testid="career-submit-btn"
            disabled={isSubmitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 text-lg"
          >
            {isSubmitting ? (
              "Submitting..."
            ) : (
              <>
                <Send className="h-5 w-5 mr-2" />
                Submit Application
              </>
            )}
          </Button>

          <p className="text-sm text-gray-500 text-center">
            We'll review your application and get back to you within 48 hours
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
};
